import React, { useEffect, useState } from "react";
import { apiFetchJson } from "../api";
import { t, tf, locale } from "../i18n";
import { SparklesIcon, ChevronRightIcon, RefreshIcon, Spinner } from "./Icons";

// Расход нейросети по пользователям — в администрировании (только для
// администратора): сколько токенов Gemini потратил каждый за сутки, неделю и
// месяц. Считает сервер по ответам Google (backend/src/geminiUsage.js).
// Сверху — больше всего за выбранный период; у кого расход за сутки резко
// вырос против его обычного дня, строка подсвечена. Свёрнуто по умолчанию,
// но итог за сутки и число всплесков видны и в заголовке.
const OPEN_STORAGE_KEY = "kl_admin_ai_usage_open";
const PERIOD_STORAGE_KEY = "kl_admin_ai_usage_period";
const PERIODS = [
  { value: "day", label: "Сутки" },
  { value: "week", label: "Неделя" },
  { value: "month", label: "Месяц" }
];

// Подписи функций — по имени, под которым сервер записывает расход.
const featureLabel = (feature) => ({
  assistant: t("AI-ассистент"),
  "reply-suggest": t("Подсказка ответа"),
  translate: t("Переводчик в поле ответа"),
  category: t("Подбор категории"),
  "photo-ad": t("Объявление по фото"),
  rewrite: t("Уникализация текста"),
  "photo-edit": t("Редактор фото")
}[feature] || feature);

const formatTokens = (value) => new Intl.NumberFormat(locale(), {
  notation: "compact",
  maximumFractionDigits: 1
}).format(Math.max(0, Number(value) || 0));
const formatCount = (value) => new Intl.NumberFormat(locale()).format(Math.max(0, Number(value) || 0));
// Во сколько раз больше обычного: от десяти — без дробной части.
const formatRatio = (value) => new Intl.NumberFormat(locale(), {
  maximumFractionDigits: Number(value) >= 10 ? 0 : 1
}).format(Number(value) || 0);

const readStored = (key, fallback, allowed) => {
  try {
    const value = localStorage.getItem(key);
    return allowed.includes(value) ? value : fallback;
  } catch {
    return fallback;
  }
};

const AdminAiUsageSection = ({ isPhoneView = false, onError }) => {
  const [open, setOpen] = useState(() => readStored(OPEN_STORAGE_KEY, "0", ["0", "1"]) === "1");
  const [period, setPeriod] = useState(() => readStored(PERIOD_STORAGE_KEY, "day", PERIODS.map((item) => item.value)));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try {
      const response = await apiFetchJson("/api/admin/ai-usage", { timeoutMs: 20000, retry: true });
      if (response?.success) setData(response);
    } catch (error) {
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  // Итог за сутки и всплески — сразу, даже когда раздел свёрнут.
  useEffect(() => {
    load();
    // Только при входе в раздел настроек.
  }, []);

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(OPEN_STORAGE_KEY, next ? "1" : "0"); } catch { /* приватное окно */ }
      return next;
    });
  };
  const changePeriod = (value) => {
    setPeriod(value);
    try { localStorage.setItem(PERIOD_STORAGE_KEY, value); } catch { /* приватное окно */ }
  };
  const toggleRow = (ownerId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(ownerId)) next.delete(ownerId); else next.add(ownerId);
      return next;
    });
  };

  const owners = Array.isArray(data?.owners) ? data.owners : [];
  const spikes = owners.filter((row) => row.spike).length;
  const dayTotal = data?.totals?.day?.tokens || 0;
  // Всплески — всегда наверху, дальше по расходу за период; кто за период
  // ничего не тратил, не показан.
  const rows = owners
    .filter((row) => row.spike || (row[period]?.requests || 0) > 0)
    .sort((a, b) => (Number(b.spike) - Number(a.spike)) || ((b[period]?.tokens || 0) - (a[period]?.tokens || 0)));
  const maxTokens = Math.max(1, ...rows.map((row) => row[period]?.tokens || 0));
  const periodTotals = data?.totals?.[period] || { tokens: 0, requests: 0, input: 0, output: 0 };

  return (
    <div className="adm-tickets adm-usage" data-phone={isPhoneView ? "1" : "0"}>
      <button type="button" className="adm-tickets-toggle kl-plain-button" onClick={toggleOpen} aria-expanded={open}>
        <SparklesIcon size={16} />
        <span className="adm-tickets-toggle-title">
          {t("Расход нейросети")}
          {data && (
            <span className="adm-tickets-toggle-sub">{tf` · за сутки ${formatTokens(dayTotal)} токенов`}</span>
          )}
          {spikes > 0 && (
            <span className="adm-usage-alert">{tf` · всплеск: ${spikes}`}</span>
          )}
        </span>
        <span className="adm-tickets-toggle-action">{open ? t("Скрыть") : t("Показать")}</span>
        <ChevronRightIcon size={16} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.18s ease" }} />
      </button>

      {open && (
        <div className="adm-tickets-body">
          <div className="adm-tickets-bar">
            <div className="adm-tickets-filters" role="tablist">
              {PERIODS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  role="tab"
                  className="adm-tickets-filter"
                  aria-pressed={period === item.value}
                  onClick={() => changePeriod(item.value)}
                >
                  {t(item.label)}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="adm-tickets-refresh kl-plain-button"
              onClick={load}
              disabled={loading}
              aria-label={t("Обновить")}
              title={t("Обновить")}
            >
              {loading ? <Spinner size={14} /> : <RefreshIcon size={14} />}
            </button>
          </div>

          {data && (
            <div className="adm-usage-total">
              <b>{formatTokens(periodTotals.tokens)}</b>
              {tf` токенов · ${formatCount(periodTotals.requests)} запросов · на входе ${formatTokens(periodTotals.input)}, на выходе ${formatTokens(periodTotals.output)}`}
            </div>
          )}
          <div className="adm-usage-note">
            {t("По ответам Google: токены на входе и на выходе вместе. Сутки, неделя и месяц — последние 24 часа, 7 и 30 дней. Подсвечен, у кого за сутки в 3 раза больше обычного дня.")}
          </div>

          {!data ? (
            <div className="adm-tickets-empty">{loading ? t("Загрузка...") : t("Не загрузилось — нажмите «Обновить»")}</div>
          ) : rows.length === 0 ? (
            <div className="adm-tickets-empty">{t("За этот период нейросетью никто не пользовался")}</div>
          ) : rows.map((row) => {
            const current = row[period] || { tokens: 0, requests: 0 };
            const isOpen = expanded.has(row.ownerId);
            const features = Object.entries(row.features || {})
              .map(([feature, windows]) => ({ feature, ...(windows?.[period] || { tokens: 0, requests: 0 }) }))
              .filter((item) => item.requests > 0)
              .sort((a, b) => b.tokens - a.tokens);
            const name = row.ownerId === "unknown"
              ? t("Без пользователя")
              : (row.username || row.ownerId);
            return (
              <div key={row.ownerId} className="adm-usage-row" data-spike={row.spike ? "1" : "0"}>
                <button
                  type="button"
                  className="adm-usage-row-main"
                  onClick={() => toggleRow(row.ownerId)}
                  aria-expanded={isOpen}
                >
                  <span className="adm-usage-head">
                    <span className="adm-usage-name">{name}</span>
                    {row.spike && (
                      <span className="adm-usage-spike">
                        {row.spikeRatio ? tf`×${formatRatio(row.spikeRatio)} к обычному дню` : t("всплеск")}
                      </span>
                    )}
                    <span className="adm-usage-tokens">{formatTokens(current.tokens)}</span>
                  </span>
                  <span className="adm-usage-bar" aria-hidden="true">
                    <span style={{ width: `${Math.max(2, Math.round((current.tokens / maxTokens) * 100))}%` }} />
                  </span>
                  <span className="adm-usage-meta">
                    {tf`${formatCount(current.requests)} запросов · сутки ${formatTokens(row.day?.tokens)} · неделя ${formatTokens(row.week?.tokens)} · месяц ${formatTokens(row.month?.tokens)}`}
                    {row.usualDayTokens > 0 && tf` · обычный день ≈ ${formatTokens(row.usualDayTokens)}`}
                  </span>
                </button>
                {isOpen && (
                  <div className="adm-usage-features">
                    {features.length === 0 ? (
                      <div className="adm-usage-feature is-empty">{t("За этот период — без расхода")}</div>
                    ) : features.map((item) => (
                      <div key={item.feature} className="adm-usage-feature">
                        <span className="adm-usage-feature-name">{featureLabel(item.feature)}</span>
                        <span className="adm-usage-feature-count">{tf`${formatCount(item.requests)} запр.`}</span>
                        <span className="adm-usage-feature-tokens">{formatTokens(item.tokens)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminAiUsageSection;
